import React, { useEffect, useState } from 'react';
import { Camera, ExternalLink, FileText, Upload, UploadCloud, X } from 'lucide-react';
import { checkFile, fmtSize, ruleHint, PHOTO_RULE } from '../fileRules';

// Upload widgets for HR files. Files are private (they need the login token), so they are opened
// from a Blob the API hands back, never from a public link.

// open a stored file in a new tab; `load` returns its Blob (e.g. () => hr.documentFileBlob(id))
export async function openStored(load) {
  const win = window.open('', '_blank'); // opened before the await so popup blockers allow it
  try {
    const url = URL.createObjectURL(await load());
    if (win) win.location.href = url; else window.location.href = url;
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    if (win) win.close();
    window.alert(e.message || 'Could not open the file');
  }
}

// an object URL for a stored file (load returns a Blob) or a picked File; null while there is none
export function useFileUrl(load, key, file) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let live = true;
    let made = null;
    setUrl(null);
    if (file) { made = URL.createObjectURL(file); setUrl(made); }
    else if (key && load) load().then(b => { if (live) { made = URL.createObjectURL(b); setUrl(made); } }).catch(() => {});
    return () => { live = false; if (made) URL.revokeObjectURL(made); };
  }, [key, file]); // eslint-disable-line react-hooks/exhaustive-deps
  return url;
}

export function FileChip({ name, size, pending, pendingNote = 'uploads on save', onOpen, onRemove }) {
  return (
    <span className={`file-chip ${pending ? 'pending' : ''}`}>
      <FileText size={14} />
      {onOpen ? <button type="button" className="file-chip-name" onClick={onOpen} title="Open">{name}<ExternalLink size={12} /></button>
              : <span className="file-chip-name">{name}</span>}
      {size ? <small>{fmtSize(size)}</small> : null}
      {pending && <small className="file-chip-tag">{pendingNote}</small>}
      {onRemove && <button type="button" className="file-chip-x" title="Remove" onClick={onRemove}><X size={13} /></button>}
    </span>
  );
}

// a dashed drop area: click anywhere on it to open the file browser (filtered to the rule's types),
// or drop files onto it; anything the rule does not allow is refused with the reason
function useRuleCheck(rule, onPick, multiple, onError) {
  const [error, setError] = useState('');
  const pick = list => {
    const files = [...(list || [])];
    if (!files.length) return;
    const bad = files.map(f => checkFile(f, rule)).find(Boolean);
    if (bad) { setError(bad); onError?.(bad); return; }
    setError('');
    onPick(multiple ? files : files[0]);
  };
  return [error, pick];
}

const dropProps = (pick, setOver) => ({
  onDragOver: e => { e.preventDefault(); setOver(true); },
  onDragLeave: () => setOver(false),
  onDrop: e => { e.preventDefault(); setOver(false); pick(e.dataTransfer.files); },
});

export function FilePicker({ rule, onPick, label = 'Choose file', multiple, onError }) {
  const [error, pick] = useRuleCheck(rule, onPick, multiple, onError);
  const [over, setOver] = useState(false);
  return (
    <>
      <label className={`dropzone ${over ? 'over' : ''}`} {...dropProps(pick, setOver)}>
        <UploadCloud size={20} />
        <span><b>{label}</b><small>{ruleHint(rule)}</small></span>
        <input type="file" hidden accept={rule.accept} multiple={multiple} onChange={e => { pick(e.target.files); e.target.value = ''; }} />
      </label>
      {error && <small className="file-error">{error}</small>}
    </>
  );
}

// one file slot: the stored file (if any), a picked one waiting for save, and a picker to add or replace
// With onUpload (a record that already exists) a picked file waits for its Upload button; without it
// (a new record) the file goes up when the record is first saved.
export function FileSlot({ label, rule, stored, onOpen, onDelete, pending, onPick, onClear, onUpload, busy }) {
  return (
    <div className="file-slot">
      {label && <span className="field-label">{label}</span>}
      {pending ? <FileChip name={pending.name} size={pending.size} pending pendingNote={onUpload ? 'not uploaded yet' : undefined} onRemove={onClear} />
               : stored ? <FileChip name={stored.name} size={stored.size} onOpen={onOpen} onRemove={onDelete} /> : null}
      {pending && onUpload && <UploadActions busy={busy} onUpload={onUpload} onRemove={onClear} />}
      <FilePicker rule={rule} onPick={onPick} label={pending || stored ? 'Replace file' : 'Choose file'} />
    </div>
  );
}

// Upload / Remove for a picked file that is waiting for confirmation
export function UploadActions({ busy, onUpload, onRemove, label = 'Upload' }) {
  const stop = fn => e => { e.preventDefault(); e.stopPropagation(); fn(); };
  return (
    <span className="upload-actions">
      <button type="button" className="btn primary small" disabled={busy} onClick={stop(onUpload)}><UploadCloud size={14} /> {busy ? 'Uploading…' : label}</button>
      <button type="button" className="btn ghost small" disabled={busy} onClick={stop(onRemove)}><X size={14} /> Remove</button>
    </span>
  );
}

// profile photo: the whole card is the click / drop area. A picked photo is only previewed; for an existing
// employee it goes up when Upload is clicked (onUpload), for a new one when the record is first saved
export function PhotoCard({ load, storedKey, pending, onPick, onClear, onDelete, onUpload, busy }) {
  const url = useFileUrl(load, storedKey, pending);
  const has = !!(pending || storedKey);
  const [error, pick] = useRuleCheck(PHOTO_RULE, onPick, false);
  const [over, setOver] = useState(false);
  const remove = e => { e.preventDefault(); e.stopPropagation(); onDelete(); };
  return (
    <label className={`photo-card ${over ? 'over' : ''} ${pending ? 'pending' : ''}`} title={has ? 'Click to choose another photo' : 'Click to choose a photo'} {...dropProps(pick, setOver)}>
      <div className="photo-preview">{url ? <img src={url} alt="Profile" /> : <Camera size={28} />}</div>
      <div className="grow">
        <b>Profile Photo</b>
        <small className="muted">{ruleHint(PHOTO_RULE)}. Used on the employee ID card and payslip.</small>
        <div className="photo-actions">
          {pending && onUpload ? (
            <>
              <UploadActions busy={busy} onUpload={onUpload} onRemove={onClear} label="Upload Photo" />
              <span className="muted small">Preview only — not uploaded yet</span>
            </>
          ) : (
            <>
              <span className="btn small"><Upload size={14} /> {has ? 'Change Photo' : 'Choose Photo'}</span>
              {pending && <button type="button" className="btn ghost small" onClick={e => { e.preventDefault(); e.stopPropagation(); onClear(); }}>Remove</button>}
              {!pending && storedKey && <button type="button" className="btn ghost small" onClick={remove}>Remove</button>}
              {pending && <span className="muted small">Uploads when you save</span>}
            </>
          )}
        </div>
        {error && <small className="file-error">{error}</small>}
      </div>
      <input type="file" hidden accept={PHOTO_RULE.accept} disabled={busy} onChange={e => { pick(e.target.files); e.target.value = ''; }} />
    </label>
  );
}

// a list/detail cell for a stored file: its name, opening it without also opening the row
export function storedFileCell(info, load) {
  if (!info) return <span className="muted">—</span>;
  return (
    <button type="button" className="file-link" title={`Open ${info.name}`} onClick={e => { e.stopPropagation(); openStored(load); }}>
      <FileText size={13} /> {info.name}
    </button>
  );
}
